import {useEffect, useState} from "react";
import {ReactIsland, type WithCommandListener} from "@/shared/reactIsland";
import {PasswordStrengthMeter} from "@/primitives/PasswordStrengthMeter";
import {PasswordRequirements} from "@/primitives/PasswordRequirements";
import {usePasswordFeedback} from "@/features/auth/usePasswordFeedback";
import type {PasswordStrengthValidation} from "@/features/auth/passwordStrengthValidation";

/** Commands the host page dispatches into the island (see `ReactIsland.dispatchCommand`). */
export type PasswordStrengthIslandCommands = {
	/** The password field's current value; recomputes and re-renders meter + checklist. */
	passwordChanged: string;
};

export interface PasswordStrengthIslandProps {
	/**
	 * Server-driven password policy (from `jsConstants`). When present, the requirements
	 * checklist is rendered under the meter; when absent, only the meter shows.
	 */
	validation?: PasswordStrengthValidation;
}

/**
 * Password-strength feedback (entropy meter + policy checklist) as a React island for
 * jQuery-driven host pages. Rendering matches the fully-React `ChangePasswordModal`;
 * all labels resolve from the global `messages` via `usePasswordFeedback`. Driven
 * imperatively by the host (`dispatchCommand("passwordChanged", value)`).
 */
export function PasswordStrengthIsland({
										   addCommandListener,
										   validation,
									   }: PasswordStrengthIslandProps & WithCommandListener<PasswordStrengthIslandCommands>) {
	const [password, setPassword] = useState("");

	useEffect(() => addCommandListener("passwordChanged", setPassword), [addCommandListener]);

	const {strength, requirements} = usePasswordFeedback(password, validation);

	return (
		<>
			<PasswordStrengthMeter
				value={strength.order}
				max={strength.maxOrder}
				level={strength.level}
				label={strength.label}
				aria-label={messages["passwordStrength.label"]}
			/>
			{requirements.length > 0 && <PasswordRequirements requirements={requirements}/>}
		</>
	);
}

/**
 * Mounts the island into the element with id `mountId` (the template's dedicated mount
 * point, placed directly beneath the password input) and returns it — drive it with
 * `dispatchCommand("passwordChanged", value)`. Each mount point carries its own id, so a
 * single page can host several independent islands. `undefined` if no such element exists.
 *
 * @param mountId - id of the mount element to render into.
 * @param validation - password policy driving the checklist; `undefined` → meter only.
 */
export function mountPasswordStrengthIsland(
	mountId: string,
	validation?: PasswordStrengthValidation,
): ReactIsland<PasswordStrengthIslandProps, PasswordStrengthIslandCommands> | undefined {
	const mount = document.getElementById(mountId);
	if (!mount) {
		return undefined;
	}

	return new ReactIsland<PasswordStrengthIslandProps, PasswordStrengthIslandCommands>(
		PasswordStrengthIsland,
		{validation},
		mount,
	);
}
